import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useNav from '../../../../hooks/useNav';
import { Breadcrumb } from '../../../../components/ui/breadcrumb';
import { styles } from '../../../../styles/tailwind-config';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../components/ui/table';
import { AlertTriangleIcon, ArrowRightIcon, CheckIcon, Loader2, PlusIcon, Trash2Icon } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import Signature from '../../../../components/ui/signature';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { useInterventionInput } from '../../../../context/InterventionInputContext';
import { useBoundStore } from '../../../../zustand';
import { AutosizeTextarea } from '../../../../components/ui/textarea-autosize';
import { useTimer } from '../../../../context/TimerContext';
import { differenceInMilliseconds, format, isValid } from 'date-fns';
import AddDocuments from './add-documents';
import { z, ZodError } from 'zod';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../../../components/ui/alert-dialog';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import axios from '../../../../lib/axios';
import { useAuth } from '../../../../context/AuthProvider';
import { useNetworkState } from '@uidotdev/usehooks';
import { getCustomSettings } from '../../../../config/custom-settings';
import { deleteDatabase } from '../../../../hooks/use-db-state';
import { Trans, useLingui } from '@lingui/react/macro'
import { isAxiosError } from 'axios';
import { safeFormat } from '../../../../lib/date';
import { createPortal } from "react-dom";
import { SignatureField } from '../../../../components/ui/signature-canvas/SignatureField';
import { SignatureStroke } from '../../../../components/ui/signature-canvas/SignatureTypes';
import Questions from '../../components/questions';

function ValidationBanner({
    open,
    errors,
    onClose,
}: { open: boolean; errors: string[]; onClose: () => void }) {
    if (!open || errors.length === 0) return null;

    const node = (
        <div
            className="fixed bottom-4 left-1/2 -translate-x-1/2 w-[min(800px,95vw)] rounded-lg border bg-white/95 backdrop-blur p-3 shadow-xl pointer-events-auto"
            style={{ zIndex: 999999 }} // higher than any overlay
            role="alert"
            aria-live="assertive"
            onMouseDown={(e) => e.stopPropagation()} // belt & braces: don't let it fall through
            onClick={(e) => e.stopPropagation()}
        >
            <div className="flex items-start gap-3">
                <AlertTriangleIcon className="h-5 w-5 text-red-600 mt-0.5" />
                <div className="flex-1">
                    <p className="font-medium text-red-700">Fout</p>
                    <ul className="mt-1 list-disc pl-5 text-sm text-neutral-700">
                        {errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                </div>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onClose(); }}
                    className="text-sm font-medium underline"
                >
                    Sluiten
                </button>
            </div>
        </div>
    );

    return createPortal(node, document.body);
}

const Invoicing = () => {
    const { i18n, t } = useLingui()
    const { auth } = useAuth()
    const { online } = useNetworkState();
    const navigate = useNavigate()
    const [searchParams] = useSearchParams();
    const type = searchParams.get('type')
    const version = searchParams.get('version') === 'v1' ? 1 : (getCustomSettings(auth.data?.databases[0].dbid)?.intervention?.invoicing?.version || 2)
    const { setNav, nav } = useNav()

    const [showCustomerCheckQuestions, setShowCustomerCheckQuestions] = useState<boolean>(false)

    const dbid = auth?.data?.databases?.[0]?.dbid;
    const isFR = i18n?.locale?.toLowerCase().startsWith('fr');
    const swapOrder = useMemo(
        () => (dbid === 1 || dbid === 7) && isFR,
        [dbid, isFR]
    );

    const pickSparepartLabel = useCallback((sp?: Sparepart | undefined) => {
        const naam = sp?.m_sPARTNAAM?.trim();
        const oms = sp?.m_sOMSCHRIJVING?.trim();
        return swapOrder ? (oms || naam || '-') : (naam || oms || '-');
    }, [swapOrder]);


    const parameters = useBoundStore((state) => state.parameters)
    const spareparts = useBoundStore((state) => state.spareparts)

    const [changed_time, setchanged_time] = useState(false);

    useEffect(() => {
        setNav({ ...nav, title: t`Overzicht interventie: callid ${searchParams.get('callid')} ${type ? ` | ${type}` : ''}` })
    }, [])

    const { resetInterventionInput, intervention, solution_extra, notification_dispatch, maintenance_contract, maintenance_contract_reason, solution_visit, warranty, follow_up_reason, photos, email_addresses, used_parts, used_spareparts, other_used_parts, start_visit, setstart_visit, end_visit, setend_visit, follow_up, reason_time_edit, setreason_time_edit, kilometers, setkilometers, distance_home, setdistance_home, home_ride_time, sethome_ride_time, name_signature, setname_signature, signature_customer, setsignature_customer, signature_technician, setsignature_technician, signature_customer_paths, setsignature_customer_paths, signature_technician_paths, setsignature_technician_paths, btw_attest, setbtw_attest, end_ride_time, start_ride_time, setend_ride_time, triptime, settriptime, worktime, setworktime } = useInterventionInput()
    const { resetTimer } = useTimer()

    const languageParams = parameters?.filter((p) => p.m_iTYPE.toString() === '3')
    const solutionParams = parameters?.filter((p) => p.m_iTYPE.toString() === '29')

    const timeDifferenceInQuarters = (start: string, end: string) => {
        const [startH, startM] = start.split(":").map(Number);
        const [endH, endM] = end.split(":").map(Number);
        const diffInMinutes = ((endH * 60 + endM) - (startH * 60 + startM));
        const roundedMinutes = Math.ceil(diffInMinutes / 15) * 15;
        return `${String(Math.floor(roundedMinutes / 60)).padStart(2, "0")}:${String(roundedMinutes % 60).padStart(2, "0")}`;
    };

    const [rideDurationInput, setRideDurationInput] = useState(
        (start_ride_time && end_ride_time)
            ? timeDifferenceInQuarters(start_ride_time, end_ride_time)
            : ''
    );

    useEffect(() => {
        if (start_ride_time && end_ride_time) {
            setRideDurationInput(timeDifferenceInQuarters(start_ride_time, end_ride_time));
        }
    }, [start_ride_time, end_ride_time]);

    const handleCloseIntervention = () => {
         if (intervention?.m_iKlantCheckID != null && intervention.m_iKlantCheckID > 0) {
            setShowCustomerCheckQuestions(true)
         }
         else {
             closeIntervention('')
         }
    }

    const location = useLocation();
    const { mutate: closeIntervention, reset: resetCloseIntervention, isPending: isClosing, isError: isErrorClosing, isSuccess: isSuccessClosing } = useMutation({
        mutationFn: async (klantcheckAnswer?: string) => {
            // IF ONE FAILS, JUST STOP & THROW ERROR
            const dbid = auth.data?.databases[0].dbid
            if (!dbid) throw new Error('No database ID found');
            if (!intervention) throw new Error('No intervention found')

            // 🛡️ Version check (blocks if outdated)
            const res = await fetch(`${process.env.PUBLIC_URL || ''}/meta.json`, { cache: 'no-cache' });
            const data = await res.json();
            const currentVersion = process.env.REACT_APP_VERSION;

            if (data.version && currentVersion && data.version !== currentVersion) {
                window.dispatchEvent(new Event('update-available'));
                throw new Error('outdated version triggered');
            }


            // Intervention
            const bodyIntervention: PostInterventionBody = {
                interventionid: intervention.m_iINTERVENTIESID.toString(),
                technicianid: intervention.m_iTECHNIEKERID.toString(),
                description: intervention.m_sOMSCHRIJVING,
                solution: `${solution_visit ? `${solution_visit}: ` : ''}${solution_extra ? solution_extra : ''}`,
                date: intervention.m_dDATUM,
                time: intervention.m_tTIJD,
                soort: intervention.m_sSOORT, status: follow_up ? '8' : '9',
                extnummer: intervention.m_sEXTNUMMER, extcomm: intervention.m_sEXTCOMM,
                intcomm: intervention.m_sINTCOMM, priority: intervention.m_iPRIORITEIT.toString(),
                dtcreate: intervention.m_dDTCREATIE,
                uscreate: intervention.m_sUSCREATIE,
                warranty: warranty || false,
                vervolgreden: follow_up_reason || '',
                dispatchmelding: notification_dispatch || '',
                ohcneed: maintenance_contract || false,
                ohc: maintenance_contract_reason || ''
            }

            // Customer
            const bodyCustomer: PostCustomersBody = {
                installlocationid: intervention.m_oKLANT.m_iINSTALLLOCATIONID.toString(),
                name: intervention.m_oKLANT.m_sNAAM,
                adres1: intervention.m_oKLANT.m_sADRES1,
                adres2: intervention.m_oKLANT.m_sADRES2,
                postcode: intervention.m_oKLANT.m_sPOSTCODE,
                city: intervention.m_oKLANT.m_sSTAD,
                kolan: intervention.m_oKLANT.m_sKOLAN,
                phone: intervention.m_oKLANT.m_sRELTEL,
                gsm: intervention.m_oKLANT.m_sRELGSM,
                email: intervention.m_oKLANT.m_sRELEMAIL,
                language: intervention.m_oKLANT.m_sTAAL
            }

            // Documents
            let btwAttests: PostKlantdocumentBody[] = btw_attest ? btw_attest?.map((attest) => ({ interventionid: intervention.m_iINTERVENTIESID.toString(), customerid: intervention.m_iKLANTID.toString(), soort: attest.soort, language: attest.language, created: intervention.m_oKLANT.m_sSTAD, handtekeningklant: (attest.handtekeningklant?.split('base64,').pop() || ''), type: attest.type, usercreate: attest.usercreate, customername: '', handtekeningtech: '' })) : []
            let signatureRapport: PostKlantdocumentBody = { interventionid: intervention.m_iINTERVENTIESID.toString(), customerid: intervention.m_iKLANTID.toString(), soort: 'Bezoekrapport', language: 'NL', created: intervention.m_oKLANT.m_sSTAD, customername: name_signature || '', handtekeningtech: signature_technician?.split('base64,').pop() || '', handtekeningklant: signature_customer?.split('base64,').pop() || '', type: 'REPORT', usercreate: '' }
            const klantDocument = [...btwAttests, signatureRapport]

            // Used parts
            let usedParts: PostUsedPartsBody[] = used_parts ? used_parts.map((part) => ({ interventionid: intervention.m_iINTERVENTIESID, partid: part.m_iWISSELSTUKID, amount: part.m_rAANTAL, bom: 1 })) : []
            if (used_spareparts) {
                let usedSpareParts: PostUsedPartsBody[] = used_spareparts ? used_spareparts.map((part) => ({ interventionid: intervention.m_iINTERVENTIESID, partid: part.partid, amount: part.amount, bom: 0 })) : []
                usedParts = usedParts.concat(usedSpareParts)
            }
            let otherUsedParts: PostOtherUsedPartsBody[] = other_used_parts ? other_used_parts.map((part) => ({ interventionid: intervention.m_iINTERVENTIESID, amount: part.amount, bom: 0, brand: part.brand, partname: part.partname, description: part.description, sku: part.sku })) : []

            // Time registration
            let timeRegistrationBody: PostTimeRegistrationBody[] = []
            const diffInMinutes = (end_visit && start_visit && !isNaN(new Date(end_visit).getTime()) && !isNaN(new Date(start_visit).getTime())) ? Math.floor((new Date(end_visit).getTime() - new Date(start_visit).getTime()) / (1000 * 60)) : 0;
            const durationInQuarters = Math.ceil(diffInMinutes / 15)
            const travelTime = start_ride_time && end_ride_time ? timeDifferenceInQuarters(start_ride_time, end_ride_time) : ''
            if (start_visit) {
                timeRegistrationBody.push({ interventionid: intervention.m_iINTERVENTIESID, technicianid: intervention.m_iTECHNIEKERID, fromtime: start_visit ? format(start_visit, "HH:mm") : '', totime: end_visit ? format(end_visit, "HH:mm") : '', duration: (start_visit && end_visit) ? formatTimeDifference(start_visit, end_visit) : 0, remark: reason_time_edit || '', aantalkwartieren: durationInQuarters, aantalkm: kilometers || 0, aantaltijd: travelTime })
            }
            if (worktime) {
                worktime.forEach((wt) => {
                    let duration = getDuration(wt.fromtime, wt.totime);
                    let quarters = timeDifferenceInQuarters(wt.fromtime, wt.totime);
                    timeRegistrationBody.push({ interventionid: intervention.m_iINTERVENTIESID, technicianid: intervention.m_iTECHNIEKERID, fromtime: wt.fromtime, totime: wt.totime, duration: duration, remark: '', aantalkwartieren: quarters, aantalkm: kilometers || 0, aantaltijd: travelTime })
                })
            }

            // Trip time
            const bodyTripTime: PostTripTimeBody[] = triptime?.map(t => (
                {
                    activity: t.activity,
                    interventionid: intervention.m_iINTERVENTIESID.toString(),
                    starttime: t.starttime,
                    endtime: t.endtime,
                    technicianid: intervention.m_iTECHNIEKERID,
                }
            )) || []

            // Emails
            const bodyEmails: PostEmailExtraBody[] = email_addresses?.map(e => (
                {
                    email: e,
                    interventionid: intervention.m_iINTERVENTIESID.toString(),
                }
            )) || []

            // Follow up
            const bodyFollowUp: PostVervolgBody[] = follow_up_reason ? [
                {
                    interventionid: intervention.m_iINTERVENTIESID,
                    login: '',
                    reason: follow_up_reason || 'Geen reden opgegeven'
                }
            ] : []

            // Dispatch messages
            const dispatchMessages: PostDispatchBody[] = notification_dispatch ? [{ interventionid: intervention.m_iINTERVENTIESID, description: notification_dispatch ?? '', usercreate: '' }] : []

            // Photos
            const photosToPost: PostPhotoBody[] = photos?.map((photo) => ({
                interventionid: intervention.m_iINTERVENTIESID,
                soort: photo.soort,
                usedpartid: typeof photo.usedpartid === 'string' ? parseInt(photo.usedpartid, 10) || 0 : photo.usedpartid,
                photo: photo.photo.split('base64,').pop(),
                machineid: typeof photo.machineid === 'string' ? parseInt(photo.machineid, 10) || 0 : photo.machineid,
                otherusedpartid: photo.otherusedpartid.toString(),
            })) || []

            // Machines
            const machineBody: PostMachineBody[] = intervention.m_aMACHINES?.map(machine => ({
                interventionid: intervention.m_iINTERVENTIESID.toString(),
                Machineid: machine.m_iMACHINEID.toString(),
                customerid: intervention.m_iKLANTID.toString(),
                merkid: machine.m_iMERKID.toString() || '',
                typeid: machine.m_iTYPEID.toString() || '',
                modelid: machine.m_iMODELID.toString() || '',
                groupid: machine.m_iGROUPID.toString() || '',
                merk: machine.m_sMERK || '',
                type: machine.m_sTYPE || '',
                model: machine.m_sMODEL || '',
                serienummer: machine.m_sSERIENR || '',
                datuminstallatie: safeFormat(machine.m_dDTINSTALL) || '',
                bouwjaar: machine.m_sBOUWJAAR || '',
                Omschrijving: machine.m_sOMS || '',
                Locatieinfo: machine.m_sLOCATIONINFO || '',
                Externeid: machine.m_sEXTNUMMER || '',
                Interneid: machine.m_sINTNUMMER || '',
                Beginonderhoud: safeFormat(machine.m_sBEGINONDERHOUD) || '',
                Datumeindgarantie: safeFormat(machine.m_dGARANTIEDATE) || '',
                naaminstallateur: machine.m_sINSTALLATEUR || ''
            })) ?? []

            // invoicingcustomer
            const invoicingcustomerBody: PostIvoicingCustomersBody = {
                customerid: intervention.m_oKLANTFACTURATIE.m_iCUSTOMERID,
                customerinvoiceid: intervention.m_oKLANTFACTURATIE.m_iKLANTFACTURATIEID,
                name: intervention.m_oKLANTFACTURATIE.m_sNAAM,
                adres1: intervention.m_oKLANTFACTURATIE.m_sADRES1,
                adres2: intervention.m_oKLANTFACTURATIE.m_sADRES2,
                postcode: intervention.m_oKLANTFACTURATIE.m_sPOSTCODE,
                city: intervention.m_oKLANTFACTURATIE.m_sSTAD,
                kolan: intervention.m_oKLANTFACTURATIE.m_sKOLAN,
                phone: intervention.m_oKLANTFACTURATIE.m_sTELEFOON,
                gsm: intervention.m_oKLANTFACTURATIE.m_sGSM,
                email: intervention.m_oKLANTFACTURATIE.m_sEMAIL,
                vatnumber: intervention.m_oKLANTFACTURATIE.m_sBTWNUMMER,
                extnumber: intervention.m_oKLANTFACTURATIE.m_sEXTNUMMER,
            }

            //ohc
            const ohcBody: PostOhcBody[] =
                maintenance_contract === true && maintenance_contract_reason?.trim()
                    ? [
                        {
                            interventionid: intervention.m_iINTERVENTIESID.toString(),
                            reason: maintenance_contract_reason,
                            login: intervention.m_oTECHNIEKER?.m_sLOGIN,
                            senttoserver: 0,
                        },
                    ]
                    : [];

            await postInterventionComplete(dbid, {
                intervention: bodyIntervention,
                customer: bodyCustomer,
                documents: klantDocument,
                usedparts: usedParts,
                otherusedparts: otherUsedParts,
                timeregistrations: timeRegistrationBody,
                triptimes: bodyTripTime,
                emails: bodyEmails,
                followup: bodyFollowUp,
                messages: dispatchMessages,
                ohc: ohcBody,
                photos: photosToPost,
                machines: machineBody,
                customerinvoice: invoicingcustomerBody,
                klantcheck: {
                    klantcheckid: intervention.m_iKlantCheckID ?? 0,
                    answer: klantcheckAnswer ?? ''
                }
            })
        },
        onError: (err) => {
            if (isAxiosError(err)) {
                const responseData = err.response?.data;
                let message = 'An error occurred';

                if (typeof responseData === 'string') {
                    message = responseData; // raw string error message
                } else if (typeof responseData === 'object' && responseData !== null) {
                    message = responseData.message || JSON.stringify(responseData);
                }

                toast.error(message, { duration: 5000 });
            } else if (err instanceof Error) {
                toast.error(err.message, { duration: 5000 });
            } else {
                toast.error(String(err), { duration: 5000 });
            }
            setTimeout(() => { resetCloseIntervention() }, 5000)
        },
        onSuccess: () => {
            toast.success(t`Interventie succesvol gesloten`)
            resetInterventionInput()
            deleteDatabase(`mountq-${intervention?.m_iCALLID}`)
            resetTimer()
            navigate('/startpagina')
        }
    })

    function getTotalDuration(timeIntervals: { from: string, to: string }[]) {
        let totalMinutes = 0;

        timeIntervals.forEach(({ from, to }) => {
            const [startHours, startMinutes] = from.split(':').map(Number);
            const [endHours, endMinutes] = to.split(':').map(Number);

            const startInMinutes = startHours * 60 + startMinutes;
            const endInMinutes = endHours * 60 + endMinutes;

            let durationInMinutes = endInMinutes - startInMinutes;
            if (durationInMinutes < 0) {
                durationInMinutes += 24 * 60; // Handle cases where end time is past midnight
            }

            totalMinutes += durationInMinutes;
        });

        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }

    function getDuration(startTime: string, endTime: string) {
        const [startHours, startMinutes] = startTime.split(':').map(Number);
        const [endHours, endMinutes] = endTime.split(':').map(Number);

        const startInMinutes = startHours * 60 + startMinutes;
        const endInMinutes = endHours * 60 + endMinutes;

        let durationInMinutes = endInMinutes - startInMinutes;
        if (durationInMinutes < 0) {
            durationInMinutes += 24 * 60; // Handle cases where end time is past midnight
        }

        const hours = Math.floor(durationInMinutes / 60);
        const minutes = durationInMinutes % 60;

        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }

    function formatTimeDifference(startDate: Date, endDate: Date): string {
        // Get the difference in milliseconds
        const difference = differenceInMilliseconds(endDate, startDate);

        // Convert milliseconds to hours and minutes
        const totalMinutes = Math.floor(difference / (1000 * 60));
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        // Pad hours and minutes to always display two digits
        const formattedHours = String(hours).padStart(2, '0');
        const formattedMinutes = String(minutes).padStart(2, '0');

        return `${formattedHours}:${formattedMinutes}`;
    }

    const addTime = (time: string, duration: string) => {
        const [hours, minutes] = time.split(":").map(Number);
        const [dHours, dMinutes] = duration.split(":").map(Number);
        const totalMinutes = hours * 60 + minutes + dHours * 60 + dMinutes;
        return `${String(Math.floor(totalMinutes / 60) % 24).padStart(2, "0")}:${String(totalMinutes % 60).padStart(2, "0")}`;
    };

    const roundTimeToNext5Min = (time: string) => {
        const [hours, minutes] = time.split(':').map(Number);
        let totalMinutes = hours * 60 + minutes;
        totalMinutes = Math.ceil(totalMinutes / 5) * 5;

        const roundedHours = Math.floor(totalMinutes / 60);
        const roundedMinutes = totalMinutes % 60;

        return `${String(roundedHours).padStart(2, '0')}:${String(roundedMinutes).padStart(2, '0')}`;
    };

    const reasonTimeEditRequired = getCustomSettings(auth.data?.databases[0].dbid).intervention?.invoicing?.require_reason_timechange

    const validation = useMemo(() => isComplete(), [
        name_signature,
        signature_customer,
        changed_time,
        reason_time_edit,
        reasonTimeEditRequired,
    ]);

    const canSubmit = online && !isClosing;

    const [showErrors, setShowErrors] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    useEffect(() => {
        if (showErrors && validation.valid) setShowErrors(false);
    }, [showErrors, validation.valid]);

    function isComplete() {
        try {

            if (reasonTimeEditRequired) {
                if (changed_time) {
                    z.object({
                        reason_time_edit: z.string({ message: t`Reden tijdsaanpassing is verplicht` }).min(1, { message: t`Reden tijdsaanpassing is verplicht` }),
                    }).parse({
                        reason_time_edit: reason_time_edit
                    })
                }
            }

            z.object({
                name_customer: z.string({ message: t`Klantnaam is verplicht` }).min(1, { message: t`Klantnaam is verplicht` }),
                signature_customer: z.string().min(5000, { message: t`Klant handtekening is verplicht` }),
            }).parse({
                name_customer: name_signature,
                signature_customer: signature_customer,
            })
            return { valid: true, errors: [] }
        } catch (e) {
            let errors: string[] = []
            if (e instanceof ZodError) {
                errors = e.errors.map((e) => e.message)
            }
            return { valid: false, errors: errors }
        }
    }


    return (
        <div className='flex flex-col gap-8 p-4'>
            <Breadcrumb items={[{ title: <Link to={`/intervention/start?callid=${searchParams.get('callid')}`}><Trans>Interventie {searchParams.get('callid')}</Trans></Link> }, { title: <Link to=""><Trans>Overzicht interventie</Trans></Link> }]} />
            <h1 className={`${styles.h1}`}><Trans>Overzicht interventie</Trans></h1>

            <div className='flex flex-col gap-2'>
                <h2 className={`${styles.h2}`}><Trans>Klant informatie</Trans></h2>
                <div>
                    <p className='font-medium text-neutral-500'><Trans>Naam</Trans></p>
                    <p className='font-medium text-neutral-800'>{intervention?.m_oKLANT?.m_sNAAM || '-'}</p>
                </div>
                <div>
                    <p className='font-medium text-neutral-500'><Trans>Adres</Trans></p>
                    <p className='font-medium text-neutral-800'>{intervention?.m_oKLANT.m_sADRES1} {intervention?.m_oKLANT.m_sNR}, {intervention?.m_oKLANT.m_sPOSTCODE} {intervention?.m_oKLANT.m_sSTAD}</p>
                </div>
                <div>
                    <p className='font-medium text-neutral-500'><Trans>Taal (documenten)</Trans></p>
                    <p className='font-medium text-neutral-800'>{(intervention?.m_oKLANT.m_sTAAL ? (languageParams?.find((p) => p.m_sCODE.toString() === intervention?.m_oKLANT.m_sTAAL.toString())?.m_sVALUE || intervention?.m_oKLANT.m_sTAAL) : '-')}</p>
                </div>
                <div>
                    <p className='font-medium text-neutral-500'><Trans>Telefoon</Trans></p>
                    <p className='font-medium text-neutral-800'>{intervention?.m_oKLANT.m_sRELTEL || '-'}</p>
                </div>
                <div>
                    <p className='font-medium text-neutral-500'><Trans>GSM</Trans></p>
                    <p className='font-medium text-neutral-800'>{intervention?.m_oKLANT.m_sRELGSM || '-'}</p>
                </div>
                <div>
                    <p className='font-medium text-neutral-500'><Trans>E-mailadres</Trans></p>
                    <p className='font-medium text-neutral-800'>{intervention?.m_oKLANT.m_sRELEMAIL || '-'}</p>
                </div>
            </div>

            <div className='flex flex-col gap-2'>
                <h2 className={`${styles.h2}`}><Trans>Korte beschrijving van de interventie</Trans></h2>
                <div>
                    <p className='font-medium text-neutral-500'><Trans>Reden</Trans></p>
                    <p className='font-medium text-neutral-800'>{intervention?.m_sOMSCHRIJVING || '-'}</p>
                </div>
                <div>
                    <p className='font-medium text-neutral-500'><Trans>Oplossing</Trans></p>
                    <p className='font-medium text-neutral-800'>
                        {solution_visit && (solutionParams ? solutionParams.find((p) => p.m_iPARAMID.toString() === solution_visit)?.m_sVALUE : solution_visit)}
                        {solution_extra && <><br />{solution_extra}</>} {(!solution_visit && !solution_extra) && t`Geen oplossing`}
                    </p>
                </div>
            </div>

            <div className='flex flex-col gap-2'>
                <h2 className={`${styles.h2}`}><Trans>Gebruikte wisselstukken</Trans></h2>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead><Trans>Artikelnr</Trans></TableHead>
                            <TableHead><Trans>Beschrijving</Trans></TableHead>
                            <TableHead><Trans>Aantal</Trans></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>

                        {used_parts?.map((part, i) => {
                            const sparepart = spareparts?.find((sparepart) => sparepart.m_iWISSELSTUKID === part.m_iWISSELSTUKID)
                            const cellContent = part?.m_sPARTNAME?.trim() || pickSparepartLabel(sparepart);

                            return (
                                <TableRow key={`used-part-${i}`}>
                                    {/* <TableCell></TableCell> */}
                                    <TableCell>{sparepart?.m_sSKU}</TableCell>
                                    <TableCell>{cellContent}</TableCell>
                                    <TableCell><p>{part.m_rAANTAL || '0'}</p></TableCell>
                                </TableRow>
                            )
                        })}

                        {used_spareparts?.map((part, i) => {
                            const sparepart = spareparts?.find((sparepart) => sparepart.m_iWISSELSTUKID.toString() === part.partid.toString())
                            const cellContent = pickSparepartLabel(sparepart);
                            return (
                                <TableRow key={`used-sparepart-${i}`}>
                                    {/* <TableCell></TableCell> */}
                                    <TableCell>{sparepart?.m_sSKU}</TableCell>
                                    <TableCell>{cellContent}</TableCell>
                                    <TableCell><p>{part.amount || '0'}</p></TableCell>
                                </TableRow>
                            )
                        })}

                        {other_used_parts?.map((part, i) => {
                            return (
                                <TableRow key={`other-used-sparepart-${i}`}>
                                    {/* <TableCell></TableCell> */}
                                    <TableCell>{part?.sku}</TableCell>
                                    <TableCell>{part?.partname} {part?.description}</TableCell>
                                    <TableCell><p>{part.amount || '0'}</p></TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </div>

            <div className='flex flex-col gap-4'>
                <div className='flex items-center justify-between gap-4'>
                    <h2 className={`${styles.h2}`}><Trans>Gepresteerde tijd</Trans></h2>
                    <Button onClick={() => { setworktime([...worktime || [], { fromtime: '', totime: '' }]) }} ><PlusIcon className='w-4 h-4' /><Trans>Nieuwe werktijd</Trans></Button>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <p className="text-sm font-medium text-neutral-500 whitespace-nowrap">
                            <Trans>Start bezoek</Trans>
                        </p>
                        <input
                            onBlur={(e) => {
                                setchanged_time(true)
                                const value = roundTimeToNext5Min(e.currentTarget.value)
                                let d = new Date()
                                d.setHours(Number(value.split(':')[0]))
                                d.setMinutes(Number(value.split(':')[1]))
                                setstart_visit(d)
                            }}
                            defaultValue={(start_visit && isValid(start_visit)) ? format(start_visit, "HH:mm") : ""}
                            type="time"
                            className="px-1 font-medium bg-transparent border rounded-sm text-neutral-800 min-w-20 w-full sm:w-auto"
                        />
                    </div>

                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <p className="text-sm font-medium text-neutral-500 whitespace-nowrap">
                            <Trans>Einde bezoek</Trans>
                        </p>
                        <input
                            onBlur={(e) => {
                                setchanged_time(true)
                                const value = roundTimeToNext5Min(e.currentTarget.value)
                                let d = new Date()
                                d.setHours(Number(value.split(':')[0]))
                                d.setMinutes(Number(value.split(':')[1]))
                                setend_visit(d)
                            }}
                            defaultValue={(end_visit && isValid(end_visit)) ? format(end_visit, "HH:mm") : ""}
                            type="time"
                            className="px-1 font-medium bg-transparent border rounded-sm text-neutral-800 min-w-20 w-full sm:w-auto"
                        />
                    </div>
                </div>

                {worktime && worktime.map((wt, index) => (
                    <div className='flex items-center gap-4' key={`worktime-${index}`}>
                        <div className='flex items-center gap-2'>
                            <p className='text-sm font-medium text-neutral-500'><Trans>Start bezoek</Trans></p>
                            <input
                                onBlur={(e) => {
                                    let updatedWorktime = worktime ? [...worktime] : []
                                    updatedWorktime[index].fromtime = roundTimeToNext5Min(e.target.value);
                                    setworktime(updatedWorktime)
                                }}
                                defaultValue={wt.fromtime}
                                type="time"
                                className='px-1 font-medium bg-transparent border rounded-sm text-neutral-800 min-w-20'
                            />
                        </div>
                        <div className='flex items-center gap-2'>
                            <p className='text-sm font-medium text-neutral-500'><Trans>Einde bezoek</Trans></p>
                            <input
                                onBlur={(e) => {
                                    let updatedWorktime = worktime ? [...worktime] : []
                                    updatedWorktime[index].totime = roundTimeToNext5Min(e.target.value);
                                    setworktime(updatedWorktime)
                                }}
                                defaultValue={wt.totime}
                                type="time"
                                className='px-1 font-medium bg-transparent border rounded-sm text-neutral-800 min-w-20'
                            />
                        </div>
                        <button onClick={() => { setworktime(worktime.filter((w, i) => i !== index)) }}><Trash2Icon className='w-4 h-4 text-red-600' /></button>
                    </div>
                ))}

                {worktime &&
                    <div className='flex items-center gap-2'>
                        <p className='text-sm font-medium text-neutral-500'><Trans>Duur bezoek</Trans></p>
                        {worktime
                            ? <p className='px-1 font-medium border rounded-sm bg-neutral-100 text-neutral-600'>{addTime(getTotalDuration(worktime?.map((wt) => ({ from: wt.fromtime || '00:00', to: wt.totime || '00:00' })) || []), ((start_visit && end_visit && isValid(start_visit) && isValid(end_visit)) ? formatTimeDifference(start_visit, end_visit) : ""))}</p>
                            :
                            <input
                                value={(start_visit && end_visit && isValid(start_visit) && isValid(end_visit)) ? formatTimeDifference(start_visit, end_visit) : ""}
                                disabled
                                className='font-medium bg-transparent text-neutral-800'
                            />
                        }
                    </div>
                }

                {getCustomSettings(auth.data?.databases[0].dbid).intervention?.invoicing?.enable_triptime
                    ?
                    <div className='space-y-1 text-neutral-600'>
                        <div className='flex items-center justify-between gap-2'>
                            <p className='font-semibold'><Trans>Tijd voor verplaatsing</Trans></p>
                            <Button onClick={() => { settriptime([...triptime || [], { starttime: '', endtime: '', activity: '', interventionid: intervention?.m_iINTERVENTIESID.toString() || '', technicianid: auth.data?.databases[0].technicianid.toString() || '' }]) }}>
                                <PlusIcon className='w-4 h-4' />
                                <Trans>Nieuwe verplaatsing</Trans>
                            </Button>
                        </div>

                        {triptime?.map((tt, index) => (
                            <div className='flex items-center gap-2' key={`triptime-${index}`}>
                                <Label><Trans>Starttijd</Trans></Label>
                                <input
                                    onBlur={(e) => {
                                        let tmp_tt = triptime ? [...triptime] : []
                                        let tt = triptime ? triptime[index] : undefined
                                        if (tt) {
                                            tt.starttime = roundTimeToNext5Min(e.currentTarget.value)
                                        } else {
                                            tt = { starttime: roundTimeToNext5Min(e.currentTarget.value), endtime: '', activity: '', interventionid: intervention?.m_iINTERVENTIESID.toString() || '', technicianid: auth.data?.databases[index].technicianid.toString() || '' }
                                        }
                                        tmp_tt[index] = tt
                                        settriptime(tmp_tt)
                                    }}
                                    defaultValue={triptime ? triptime[index]?.starttime : ''}
                                    type='time'
                                    className='px-1 font-medium bg-transparent border rounded-sm text-neutral-800 min-w-20'
                                />

                                <ArrowRightIcon className='w-4 h-4' />

                                <Label><Trans>Eindtijd</Trans></Label>
                                <input
                                    onBlur={(e) => {
                                        let tmp_tt = triptime ? [...triptime] : []
                                        let tt = triptime ? triptime[index] : undefined
                                        if (tt) {
                                            tt.endtime = roundTimeToNext5Min(e.currentTarget.value)
                                        } else {
                                            tt = { endtime: roundTimeToNext5Min(e.currentTarget.value), starttime: '', activity: '', interventionid: intervention?.m_iINTERVENTIESID.toString() || '', technicianid: auth.data?.databases[index].technicianid.toString() || '' }
                                        }
                                        tmp_tt[index] = tt
                                        settriptime(tmp_tt)
                                    }}
                                    defaultValue={triptime ? triptime[index]?.endtime : ''}
                                    type='time'
                                    className='px-1 font-medium bg-transparent border rounded-sm text-neutral-800 min-w-20'
                                />

                                <button onClick={() => { settriptime(triptime.filter((w, i) => i !== index)) }}><Trash2Icon className='w-4 h-4 text-red-600' /></button>
                            </div>
                        ))}
                    </div>
                    :
                    <>
                        <div>
                            <p className='text-sm font-medium text-neutral-500'><Trans>Aantal kilometers</Trans></p>
                            <input value={kilometers || ''} onChange={(e) => { setkilometers(Number(e.target.value)) }} type="number" className='px-1 font-medium bg-transparent border rounded-sm text-neutral-800' />
                        </div>
                        <div>
                            <p className='text-sm font-medium text-neutral-500'>
                                <Trans>Tijd verplaatsing</Trans>
                            </p>
                            <input
                                type='time'
                                className='px-1 font-medium bg-transparent border rounded-sm text-neutral-800'
                                value={rideDurationInput}
                                onChange={(e) => {
                                    setRideDurationInput(e.currentTarget.value);
                                }}
                                onBlur={(e) => {
                                    //setchanged_time(true);

                                    const raw = e.currentTarget.value;

                                    const newEnd = addTime((start_ride_time || '00:00'), raw);
                                    setend_ride_time(newEnd);

                                    const snapped = (start_ride_time)
                                        ? timeDifferenceInQuarters(start_ride_time, newEnd)
                                        : raw;

                                    setRideDurationInput(snapped);
                                }}
                            />
                        </div>
                    </>
                }

                {changed_time &&
                    <div>
                        <p className='text-sm font-medium text-neutral-500'><Trans>Reden tijdsaanpassingen</Trans>{reasonTimeEditRequired && <span className='ml-1 text-red-600'>*</span>}</p>
                        <AutosizeTextarea value={reason_time_edit || ''} onChange={(e) => { setreason_time_edit(e.target.value) }} />
                    </div>
                }
            </div>

            <div className='flex flex-col gap-2'>

                <div className='flex flex-col items-center justify-between gap-4 sm:flex-row'>
                    <h2 className={`${styles.h2}`}><Trans>Documenten</Trans></h2>
                    <AddDocuments />
                </div>

                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead><Trans>Documentnaam</Trans></TableHead>
                            <TableHead><Trans>Toegevoegd op</Trans></TableHead>
                            <TableHead></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {btw_attest && btw_attest.map((attest, i) => (
                            <TableRow key={`btw-attest-${i}`}>
                                <TableCell><Trans>BTW attest</Trans></TableCell>
                                <TableCell>{attest.datecreate || ''}</TableCell>
                                <TableCell>
                                    <div className='flex items-center justify-end'>
                                        <button onClick={() => { setbtw_attest(null) }}><Trash2Icon className='w-4 h-4 text-red-600' /></button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                        {(!btw_attest || btw_attest.length === 0) &&
                            <TableRow>
                                <TableCell colSpan={99}><Trans>Geen documenten toegevoegd</Trans></TableCell>
                            </TableRow>
                        }
                    </TableBody>
                </Table>
            </div>

            {/* <div className='flex flex-col gap-2'>
                <h2 className={`${styles.h2}`}><Trans>Handtekening</Trans></h2>
                <div>
                    <Label><Trans>Naam klant</Trans><span className='ml-1 text-red-600'>*</span></Label>
                    <Input value={name_signature || ''} onChange={(e) => { setname_signature(e.target.value) }} placeholder={t`Typ uw naam...`} />
                </div>
                <Signature
                    height={200}
                    mobileHeight={200}
                    label={t`Handtekening klant *`}
                    defaultPath={signature_customer_paths || undefined}
                    onChangePaths={paths => setsignature_customer_paths(paths)}
                    onChange={base64 => setsignature_customer(base64.split('base64,')[1] || base64)}
                    expandableOnMobile={false}
                />

                <Signature
                    height={200}
                    mobileHeight={200}
                    defaultPath={signature_technician_paths || undefined}
                    label={t`Handtekening technieker`}
                    onChangePaths={(paths) => { setsignature_technician_paths(paths) }}
                    onChange={(base64) => { setsignature_technician(base64.split('base64,')[1] || base64) }}
                    expandableOnMobile={true}
                />
            </div> */}

            <div className="space-y-4">
                <h2 className={styles.h2}>
                    <Trans>Handtekening</Trans>
                </h2>

                <div>
                    <Label>
                        <Trans>Naam klant</Trans>
                        <span className="ml-1 text-red-600">*</span>
                    </Label>
                    <Input
                        value={name_signature || ""}
                        onChange={(e) => setname_signature(e.target.value)}
                        placeholder={t`Typ uw naam...`}
                    />
                </div>

                <SignatureField
                    label={t`Handtekening klant *`}
                    valueStrokes={(signature_customer_paths as unknown as SignatureStroke[]) || []}
                    valuePng={
                        signature_customer ? `data:image/png;base64,${signature_customer}` : ""
                    }
                    onChange={(strokes, pngDataUrl) => {
                        setsignature_customer_paths(strokes as any);
                        const raw = pngDataUrl.split("base64,")[1] || "";
                        setsignature_customer(raw);
                    }}
                />

                <SignatureField
                    label={t`Handtekening technieker`}
                    valueStrokes={(signature_technician_paths as unknown as SignatureStroke[]) || []}
                    valuePng={
                        signature_technician ? `data:image/png;base64,${signature_technician}` : ""
                    }
                    onChange={(strokes, pngDataUrl) => {
                        setsignature_technician_paths(strokes as any);
                        const raw = pngDataUrl.split("base64,")[1] || "";
                        setsignature_technician(raw);
                    }}
                />
            </div>

            <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <AlertDialogTrigger asChild>
                    <Button
                        onPointerDown={(e) => {
                            if (!validation.valid) {
                                e.preventDefault();
                                setShowErrors(true);
                            }
                        }}
                        onClick={() => {
                            if (validation.valid) setDialogOpen(true);
                        }}
                        disabled={!canSubmit}
                    >
                        {isClosing && <Loader2 className="w-4 h-4 animate-spin" />}
                        {isErrorClosing && <AlertTriangleIcon className="w-4 h-4" />}
                        {isSuccessClosing && <CheckIcon className="w-4 h-4" />}
                        {isClosing ? t`Bezoek aan het sluiten...` : (online ? t`Huidige bezoek sluiten` : t`Internet nodig om bezoek te sluiten`)}
                    </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle><Trans>Wilt u deze interventie definitief afsluiten?</Trans></AlertDialogTitle>
                        <AlertDialogDescription><Trans>U kan deze actie niet ongedaan maken</Trans></AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel><Trans>Annuleren</Trans></AlertDialogCancel>
                        <AlertDialogAction onClick={() => { handleCloseIntervention() }} disabled={!isComplete().valid || isClosing || isErrorClosing || isSuccessClosing}><Trans>Afsluiten</Trans></AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Questions
                open={showCustomerCheckQuestions}
                onOpenChange={setShowCustomerCheckQuestions}
                klantcheckid={intervention?.m_iKlantCheckID ?? 0}
                onSubmit={(_klantcheckid, answer) => { closeIntervention(answer) }}
            />

            <ValidationBanner
                open={showErrors && !validation.valid}
                errors={validation.errors}
                onClose={() => setShowErrors(false)}
            />

        </div>
    )
}

export default Invoicing

type PostInterventionBody = {
    interventionid: string,
    technicianid: string,
    description: string,
    solution: string,
    date: string,
    time: string,
    soort: string,
    vervolgreden: string;
    dispatchmelding: string;
    ohcneed: boolean;
    ohc: string;
    status: string,
    extnummer: string,
    extcomm: string,
    intcomm: string,
    priority: string,
    dtcreate: string,
    uscreate: string,
    warranty: boolean
}
async function postIntervention(dbid: string | number, body: PostInterventionBody) {
    const { data } = await axios.post(`intervention?dbid=${dbid}`, {
        interventionid: body.interventionid,
        technicianid: body.technicianid,
        description: body.description,
        solution: body.solution,
        date: body.date,
        time: body.time,
        soort: body.soort,
        warranty: body.warranty,
        vervolgreden: body.vervolgreden,
        dispatchmelding: body.dispatchmelding,
        ohcneed: body.ohcneed,
        ohc: body.ohc,
        status: body.status,
        extnummer: body.extnummer,
        extcomm: body.extcomm,
        intcomm: body.intcomm,
        priority: body.priority,
        dtcreate: body.dtcreate,
        uscreate: body.uscreate,
        timestamp: format(new Date(), 'yyyyMMddHHmmss'),
    })
    return data
}

type PostCustomersBody = {
    installlocationid: string;
    name: string;
    adres1: string;
    adres2: string;
    postcode: string;
    city: string;
    kolan: string;
    phone: string;
    gsm: string;
    email: string;
    language: string;
}
async function postCustomers(dbid: string | number, body: PostCustomersBody) {
    const { data } = await axios.post(`customers?dbid=${dbid}`, {
        installlocationid: body.installlocationid,
        name: body.name,
        adres1: body.adres1,
        adres2: body.adres2,
        postcode: body.postcode,
        city: body.city,
        kolan: body.kolan,
        phone: body.phone,
        gsm: body.gsm,
        email: body.email,
        language: body.language,
        timestamp: format(new Date(), 'yyyyMMddHHmmss'),
    })
    return data
}

type PostTimeRegistrationBody = {
    interventionid: string | number;
    technicianid: string | number;
    fromtime: string | number;
    totime: string | number;
    duration: string | number;
    remark: string | number;
    aantalkwartieren: string | number;
    aantalkm: string | number;
    aantaltijd: string | number;
}
async function postTimeRegistration(dbid: string | number, body: PostTimeRegistrationBody[]) {
    const { data } = await axios.post(`timeregistration?dbid=${dbid}`, {
        timeregistrations: body.map((object) => (
            {
                interventionid: object.interventionid,
                technicianid: object.technicianid,
                fromtime: object.fromtime,
                totime: object.totime,
                duration: object.duration,
                remark: object.remark,
                aantalkwartieren: object.aantalkwartieren,
                aantalverplaatsing: 0,
                aantalkm: object.aantalkm,
                aantaltijd: object.aantaltijd,
                played: '',
                pauzed: '',
                report: '',
                prestatiecode: '',
                fromdate: format(new Date(), 'yyyyMMdd'),
                todate: format(new Date(), 'yyyyMMdd'),
                timestamp: format(new Date(), 'yyyyMMddHHmmss'),
            }
        ))
    })
    return data
}

type PostTripTimeBody = {
    technicianid: string | number;
    interventionid: string | number;
    starttime: string;
    endtime: string;
    activity: string;
}
async function postTripTime(dbid: string | number, body: PostTripTimeBody[]) {
    const { data } = await axios.post(`triptime?dbid=${dbid}`, {
        triptimes: body.map((object) => ({
            ...object,
            date: format(new Date(), "yyyyMMdd"),
            duration: "0",
            timestamp: format(new Date(), "yyyyMMddHHmmss"),
        }))
    })
    return data
}

type PostPhotoBody = {
    interventionid: string | number;
    soort: string;
    photo: string | undefined;
    usedpartid?: number;
    machineid?: number;
    otherusedpartid?: string;
}
async function postPhoto(dbid: string | number, body: PostPhotoBody[]) {
    const { data } = await axios.post(`photo?dbid=${dbid}`, {
        photos: body.map((object) => ({
            ...object,
            timestamp: format(new Date(), "yyyyMMddHHmmss"),
            attestid: '',
            docid: ''
        }))
    })
    return data
}

type PostUsedPartsBody = {
    interventionid: string | number;
    partid: string | number;
    amount: string | number;
    bom: 1 | 0;
}
async function postUsedParts(dbid: string | number, body: PostUsedPartsBody[]) {
    const { data } = await axios.post(`usedparts?dbid=${dbid}`, {
        usedparts: body.map((object) => ({
            ...object,
            warranty: 0,
            toprice: '',
            machineid: '',
            timestamp: format(new Date(), 'yyyyMMddHHmmss'),
        }))
    })
    return data
}

type PostOtherUsedPartsBody = {
    interventionid: string | number;
    brand: string;
    sku: string;
    partname: string;
    description: string;
    amount: string | number;
    bom: 1 | 0;
}
async function postOtherUsedParts(dbid: string | number, body: PostOtherUsedPartsBody[]) {
    const { data } = await axios.post(`otherusedparts?dbid=${dbid}`, {
        otherusedparts: body.map((object) => ({
            ...object,
            warranty: 0,
            toprice: '',
            machineid: '',
            timestamp: format(new Date(), 'yyyyMMddHHmmss'),
        }))
    })
    return data
}

type PostEmailExtraBody = {
    interventionid: string | number;
    email: string;
}
async function postEmailExtra(dbid: string | number, body: PostEmailExtraBody[]) {
    const { data } = await axios.post(`emailextra?dbid=${dbid}`, {
        emails: body
    })
    return data
}

type PostDispatchBody = {
    interventionid: string | number;
    description: string;
    usercreate: string;
}
async function postDispatch(dbid: string | number, body: PostDispatchBody[]) {
    const { data } = await axios.post(`dispatch?dbid=${dbid}`, {
        messages: body
    })
    return data
}

type PostVervolgBody = {
    interventionid: string | number;
    reason: string;
    login: string;
}
async function postVervolg(dbid: string | number, body: PostVervolgBody[]) {
    const { data } = await axios.post(`vervolg?dbid=${dbid}`, {
        followup: body.map((object) => ({
            ...object,
            date: format(new Date(), "yyyyMMdd"),
            time: format(new Date(), "HH:mm"),
            informed: '',
            timestamp: format(new Date(), "yyyyMMddHHmmss"),
        }))
    })
    return data
}

type PostOhcBody = {
    interventionid: string | number;
    reason: string;
    login: string;
    senttoserver: number; // 0
    //timestamp: string;
}
async function postOhc(dbid: string | number, body: PostOhcBody[]) {
    const { data } = await axios.post(`ohc?dbid=${dbid}`, {
        ohc: body.map((object) => ({
            interventionid: object.interventionid,
            reason: object.reason,
            login: object.login,
            senttoserver: object.senttoserver
        }))
    })
    return data
}

type PostKlantdocumentBody = {
    interventionid: string;
    customerid: string;
    soort: string;
    type: string;
    language: string;
    created: string;
    usercreate: string;
    customername: string
    handtekeningklant: string;
    handtekeningtech: string;
}
async function postKlantDocument(dbid: string | number, body: PostKlantdocumentBody[]) {
    const { data } = await axios.post(`klantdocument?dbid=${dbid}`, {
        documents: body.map((object) => ({
            ...object,
            datecreate: format(new Date(), 'yyyyMMdd'),
            price: '',
            sent: 0,
            timestamp: format(new Date(), 'yyyyMMddHHmmss'),
        }))
    })
    return data
}

type PostMachineBody = {
    interventionid: string;
    Machineid: string;
    customerid: string;
    merkid: string;
    typeid: string;
    modelid: string;
    groupid: string;
    merk: string;
    type: string;
    model: string;
    serienummer: string;
    datuminstallatie: string;
    bouwjaar: string
    Omschrijving: string;
    Locatieinfo: string;
    Externeid: string;
    Interneid: string;
    Beginonderhoud: string;
    Datumeindgarantie: string;
    naaminstallateur: string;
}

type PostIvoicingCustomersBody = {
    customerid: number;
    customerinvoiceid: number;
    name: string;
    adres1: string;
    adres2: string;
    postcode: string;
    city: string;
    kolan: string;
    phone: string;
    gsm: string;
    email: string;
    vatnumber: string;
    extnumber: string;
}

type PostInterventionCompleteBody = {
    intervention: PostInterventionBody
    customer: PostCustomersBody
    timeregistrations: PostTimeRegistrationBody[]
    triptimes: PostTripTimeBody[]
    photos: PostPhotoBody[]
    usedparts: PostUsedPartsBody[]
    otherusedparts: PostOtherUsedPartsBody[]
    emails: PostEmailExtraBody[]
    messages: PostDispatchBody[]
    followup: PostVervolgBody[]
    ohc: PostOhcBody[]
    documents: PostKlantdocumentBody[]
    machines: PostMachineBody[]
    customerinvoice: PostIvoicingCustomersBody
    klantcheck: {
        klantcheckid: number
        answer: string
    }
}
async function postInterventionComplete(dbid: string | number, body: PostInterventionCompleteBody) {
    const { data } = await axios.post(`intervention_v2?dbid=${dbid}`, body)
    return data
    //console.warn(JSON.stringify(body, null, 2));
}